/**
 * Two letters and a colour for anything with no picture of its own — a
 * site key, an organisation name.
 *
 * The initials rule is the sidebar switcher's, lifted here so a card and
 * the switcher that names the same thing can never disagree. The tone is
 * derived from a seed rather than stored, so it costs nothing and is
 * stable: seed it with the id, not the label, and a rename keeps the
 * colour the eye has already learned.
 */

/** Two letters for an avatar — a name's initials, an id's first letters. */
export function initialsOf(label: string): string {
  const words = label.trim().split(/[\s._-]+/).filter(Boolean);
  if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

/** Six steps of the cool neutral ramp for an initials square, no hue:
 *  the design system keeps colour for status and the one accent, so a
 *  mark tells things apart by tone, not by tint. Each step flips with
 *  `data-theme`, and each stays a soft square near the ground, as the
 *  design system's avatar is — never a solid control fill, which on the
 *  light set is a near-black block (mark.tones.test.ts measures both
 *  themes: initials at AA, tone close to the ground). Whole class
 *  strings: Tailwind only sees what is written out in full. */
export const TINTS = [
  'bg-avatar text-fg',
  'bg-wash-3 text-fg',
  'bg-tint text-fg-2',
  'bg-surface text-fg',
  'bg-raised text-fg-2',
  'bg-wash-2 text-fg',
];

/** The same seed always gets the same tint. */
export function tintOf(seed: string): string {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return TINTS[h % TINTS.length];
}
